// ADR-028 — a Stalled or Lost job has three ways back, side by side, each with one line on what it does.
// The backbone decides what is available (GET /api/jobs/:ref/closed-options runs the same rule the two
// commands do), so the screen never offers what the write would refuse.
import {useState,type ReactNode} from 'react';
import {useQuery} from '@tanstack/react-query';
import {Button} from '@aiw/ui';
import type {MessageId} from '@aiw/i18n';
import {authHeaders,fetchImpl} from '../lib/api';
import {env} from '../lib/env';
import {useT} from '../lib/i18n';
import {useProposal} from '../hooks/slice-a';
import {inputCls} from './job-bits';

type Decision={available:true;to:string;restores?:number;recorded_at?:string}|{available:false;code:string;reason:string};
type Options={job_ref:string;stage:string|null;updated_at:string;restore:Decision;opportunity:Decision};
const WHY:Record<string,MessageId>={
 not_closed:'closedJob.why.not_closed',no_history:'closedJob.why.no_history',later_change:'closedJob.why.no_history',unknown_stage:'closedJob.why.unknown_stage',
 paid_date:'closedJob.why.paid_date',close_date:'closedJob.why.close_date',aiw_origin:'closedJob.why.aiw_origin',aiw_acceptance:'closedJob.why.aiw_acceptance',
 installation:'closedJob.why.installation',money:'closedJob.why.money',
};

export function ClosedJobActions({jobRef,stage,reopen}:{jobRef:string;stage:string;reopen:ReactNode}){
 const {t}=useT();
 const q=useQuery({queryKey:['closed-options',jobRef],enabled:!env.mock,queryFn:async()=>{
  const r=await fetchImpl(`${env.apiBase}/api/jobs/${encodeURIComponent(jobRef)}/closed-options`,{headers:await authHeaders()});
  const b=await r.json() as {data?:Options;error?:string};if(!r.ok||!b.data)throw Error(b.error??t('closedJob.unavailable'));return b.data;
 }});
 const why=(d:Decision)=>d.available?null:WHY[d.code]?t(WHY[d.code]!):d.reason;
 const o=q.data;
 return <section aria-label={t('closedJob.title')} className="mb-4 rounded-lg border bg-card p-4">
  <h2 className="font-semibold">{t('closedJob.title')}</h2>
  <p className="mb-3 mt-1 text-sm text-muted-foreground">{t('closedJob.lede',{stage})}</p>
  {env.mock?<p className="text-sm text-muted-foreground">{t('closedJob.liveOnly')}</p>
   :q.isPending?<p role="status" className="text-sm">{t('closedJob.loading')}</p>
   :q.isError||!o?<div role="alert" className="space-y-2 text-sm"><p>{t('closedJob.unavailable')}</p><Button size="sm" variant="outline" onClick={()=>void q.refetch()}>{t('closedJob.retry')}</Button></div>
   :<div className="grid gap-3 md:grid-cols-2">
    <Choice title={o.restore.available?t('closedJob.restore.titleTo',{stage:o.restore.to}):t('closedJob.restore.title')} line={t('closedJob.restore.line')} blocked={why(o.restore)}
     command="restore_job_stage" args={{job_ref:jobRef,expected_updated_at:o.updated_at,...(o.restore.available?{restores:o.restore.restores,expected_to:o.restore.to}:{})}}
     done={o.restore.available?t('closedJob.restore.done',{stage:o.restore.to}):''} action={t('closedJob.restore.action')}/>
    <Choice title={t('closedJob.opportunity.title')} line={o.opportunity.available?t('closedJob.opportunity.lineTo',{stage:o.opportunity.to}):t('closedJob.opportunity.line')} blocked={why(o.opportunity)}
     command="return_job_to_opportunity" args={{job_ref:jobRef,expected_updated_at:o.updated_at,...(o.opportunity.available?{expected_to:o.opportunity.to}:{})}}
     done={t('closedJob.opportunity.done')} action={t('closedJob.opportunity.action')}/>
   </div>}
  <div className="mt-3">
   <h3 className="text-sm font-semibold">{t('closedJob.reopen.title')}</h3>
   <p className="mb-2 text-sm text-muted-foreground">{t('closedJob.reopen.line')}</p>
   {reopen}
  </div>
 </section>;
}

function Choice({title,line,blocked,command,args,done,action}:{title:string;line:string;blocked:string|null;command:string;args:Record<string,unknown>;done:string;action:string}){
 const {t}=useT();
 const proposal=useProposal();
 const [open,setOpen]=useState(false),[reason,setReason]=useState('');
 const valid=reason.trim().length>=10;
 return <div className="rounded-md border p-3">
  <h3 className="text-sm font-semibold">{title}</h3>
  <p className="mt-1 text-sm text-muted-foreground">{line}</p>
  {blocked?<p role="status" className="mt-2 text-sm">{blocked}</p>
   :!open?<Button className="mt-2" size="sm" variant="outline" onClick={()=>setOpen(true)}>{action}</Button>
   :<form className="mt-2 space-y-2 text-sm" onSubmit={async e=>{e.preventDefault();if(!valid)return;const r=await proposal.run(command,{...args,reason:reason.trim()},done);if(r?.status==='executed'||r?.status==='pending'){setOpen(false);setReason('');}}}>
    <label className="block">{t('closedJob.reason')}<input aria-label={t('closedJob.reason')} className={inputCls+' mt-1'} value={reason} onChange={e=>setReason(e.target.value)} minLength={10} maxLength={1000} required placeholder={t('closedJob.reasonHint')}/></label>
    <div className="flex gap-2"><Button size="sm" type="submit" disabled={proposal.busy||!valid}>{action}</Button><Button size="sm" variant="outline" type="button" onClick={()=>setOpen(false)}>{t('closedJob.cancel')}</Button></div>
   </form>}
  {proposal.note&&<p role={proposal.note.tone==='bad'?'alert':'status'} className="mt-2 text-sm">{proposal.note.text}</p>}
 </div>;
}
